import * as React from "react";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";
import { toast } from "sonner";
import {
  containsLetters,
  replaceLettersWithZero,
  MATRICULA_TOOLTIP_TEXT,
} from "@/utils/matriculaUtils";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> & {
  label?: string;
  showLabel?: boolean;
  helperClassName?: string;
  /** Called with the sanitized value (letters replaced by 0, non-digits removed). */
  onValueChange?: (value: string) => void;
};

/**
 * Campo de matrícula com tooltip fixo e substituição de letras por '0' em tempo real.
 * Quando o usuário tenta digitar uma letra, exibe toast e aviso temporário.
 */
export const MatriculaInput = React.forwardRef<HTMLInputElement, Props>(
  ({ label = "Matrícula", showLabel = true, id, onValueChange, helperClassName, className, ...rest }, ref) => {
    const [showHint, setShowHint] = React.useState(false);
    const hintTimer = React.useRef<number | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const hasLetter = containsLetters(raw);
      const sanitized = replaceLettersWithZero(raw);
      e.target.value = sanitized;
      if (hasLetter) {
        toast.info("Letras não são permitidas — substituídas por 0.");
        setShowHint(true);
        if (hintTimer.current) window.clearTimeout(hintTimer.current);
        hintTimer.current = window.setTimeout(() => setShowHint(false), 3500);
      }
      onValueChange?.(sanitized);
      rest.onInput?.(e as any);
    };

    React.useEffect(() => {
      return () => {
        if (hintTimer.current) window.clearTimeout(hintTimer.current);
      };
    }, []);

    const inputId = id || "matricula-input";

    return (
      <div className="space-y-1">
        {showLabel && (
          <div className="flex items-center gap-1.5">
            <Label htmlFor={inputId}>{label}</Label>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Informação sobre matrícula" className="text-muted-foreground hover:text-foreground">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {MATRICULA_TOOLTIP_TEXT}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        <Input
          id={inputId}
          ref={ref}
          inputMode="numeric"
          autoComplete="off"
          {...rest}
          className={className}
          onChange={handleChange}
        />
        <p className={helperClassName ?? "text-xs text-muted-foreground"}>
          {MATRICULA_TOOLTIP_TEXT}
        </p>
        {showHint && (
          <p className="text-xs text-orange-600 dark:text-orange-400">
            Letras não são permitidas — substituídas por 0.
          </p>
        )}
      </div>
    );
  }
);
MatriculaInput.displayName = "MatriculaInput";
